import { FormEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import { theme } from 'theme';
import { TextFilter } from 'components/common/TextFilter';
import {
  selectAccountDetails,
  selectAccountError,
  selectAccountSearchTerm,
  selectIsAccountLoading,
} from 'selectors/accountSelector';
import { searchAccountThunk, setAccountDetails, setAccountError, setAccountSearch } from 'reducers/accountReducer';
import { addLeadingZeros } from 'utils/stringUtils';
import { closeModal } from 'reducers/modalReducer';
import { LoadingButton } from 'components/common/LoadingButton';
import { CustomError } from 'types';
import { AccountSearchResult } from 'pco-product-service-internal-consumer';
import { useErrorHandling } from 'components/hooks/useErrorHandling';
import { AppDispatch, RootState } from 'store';

const styles = {
  outerContainer: {
    minHeight: '100vh',
  },
  container: {
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
  },
  innerContainer: {
    paddingTop: theme.spacing(2),
    height: '100vh',
    overflowY: 'auto',
  },
  overviewProductsContainer: {
    backgroundColor: theme.palette.common.white,
  },
};

export const SearchAccount = () => {
  const searchTerm = useSelector(selectAccountSearchTerm);
  const dispatch = useDispatch<AppDispatch>();
  const isLoading = useSelector(selectIsAccountLoading);
  const error: CustomError | undefined = useSelector(selectAccountError);
  const accountDetails: AccountSearchResult | undefined = useSelector(selectAccountDetails);
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);

  useErrorHandling(error, () => {
    dispatch(setAccountError(undefined));
    dispatch(closeModal());
  });

  useEffect(() => {
    if (accountDetails && accountDetails.accountName && accountDetails.accountNumber) {
      const { accountName, accountNumber } = accountDetails;
      window.parent.postMessage(
        {
          message: 'setAccountDetails',
          value: { accountNr: accountNumber, accountName },
        },
        CLM_URL,
      );
      //todo may need to change url for the stand alone mode, not needed for embedded iframe
      //_______________________________________________________
      dispatch(setAccountDetails(undefined));
    }
  }, [accountDetails, dispatch, CLM_URL]);

  const handleSearchClick = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchTerm) {
      const id = addLeadingZeros(searchTerm, 8);
      dispatch(searchAccountThunk(id));
    }
  };

  return (
    <Grid component="main" container sx={styles.outerContainer}>
      <Grid item xs={6} sx={[styles.innerContainer, styles.overviewProductsContainer]}>
        <Grid sx={styles.container}>
          <Grid>
            <Typography variant={'h1'}>SEARCH ACCOUNT IN CRM-CH</Typography>
          </Grid>
          <form onSubmit={handleSearchClick}>
            <Grid container>
              <Grid item xs={4}>
                <TextFilter
                  filterName={'Enter account number'}
                  placeholderText={''}
                  dispatchAction={(value: string | undefined) => {
                    dispatch(setAccountSearch(value));
                  }}
                />
              </Grid>
              <Box
                justifyContent="flex-start"
                display="flex"
                alignItems="flex-end"
                style={{ paddingBottom: theme.spacing(0.5) }}
              >
                <LoadingButton
                  id={'search-account-button'}
                  data-testid="search-account-button"
                  disabled={!searchTerm}
                  variant="contained"
                  color="primary"
                  loading={isLoading}
                  type={'submit'}
                >
                  Search account
                </LoadingButton>
              </Box>
            </Grid>
          </form>
        </Grid>
      </Grid>
      <Grid item xs={6} sx={styles.innerContainer} />
    </Grid>
  );
};
